/** The plan model — the web's mirror of the phone's route plans. Field
 * names are chosen so the eventual QR/share handoff translates losslessly;
 * anything the phone models that the web doesn't build yet (waypoint
 * kinds, day breaks, snapped leg geometry) is carried structurally so a
 * saved plan never needs a schema migration when those land.
 *
 * v1 scope: straight legs only. `mode` exists per leg from day one so the
 * DLR2 snap engine can flip individual legs without touching the store. */

/** [lon, lat] — GeoJSON axis order everywhere in the web app. */
export type LonLat = [number, number];

export type LegMode = "straight" | "snapped";

export interface PlanLeg {
  mode: LegMode;
  /** Solved geometry between this leg's endpoints, exclusive of them —
   * empty for straight legs. Filled by the snap engine later. */
  via: LonLat[];
  /** seasons.json ids (0-based) of seasonally-restricted ways this snapped
   * leg traverses — from the graph's per-way seasonClosureId (stored there
   * +1, with 0 = none; the -1 is applied at solve time so THIS field always
   * matches seasons.json's own `id`). Absent/empty on straight legs and on
   * plans saved before closures shipped. */
  closureIds?: number[];
  /** The dominant legal-route name of a snapped leg — router.ts's
   * routerWith.solve picks the most-traveled way along the solved path and
   * resolves it through the graph's name table. Used ONLY as a save-time
   * name suggestion (PlanBar's DONE prefill); never shown as route
   * geometry truth. Absent on straight legs and whenever the dominant way
   * carries no name in the source data — router.ts/solveLeg omit the key
   * rather than store an empty string. */
  name?: string;
}

/** Mirrors Trip.Waypoint on the phone: the "kind" IS the name — iOS has
 * no kind enum, it pattern-matches name prefixes. The picker vocabulary
 * there: Gate, Water crossing, Obstacle, Campsite, Fuel, Photo spot,
 * Turnoff (+ auto-generated "Camp N" / "Night N" / "Turnaround"). */
export interface Waypoint {
  at: LonLat;
  name: string;
}

export interface Plan {
  name: string;
  /** State region slug the plan was drawn in (utah, colorado, …). */
  region: string;
  /** The user's control points, in click order. */
  points: LonLat[];
  /** legs[i] connects points[i] → points[i+1]; length = points.length-1. */
  legs: PlanLeg[];
  waypoints: Waypoint[];
  /** CONTROL-POINT indices where each overnight falls — "Day N ends at
   * points[i]". Ascending, deduped, each in (0, points.length-1). Matches
   * the share spec's `d` field (docs/SHARE_LINK_SPEC.md) and what iOS
   * PlanImport consumes — NOT flattened-geometry indices, which would go
   * stale every time a leg re-solves. */
  dayBreaks?: number[];
  /** Free-text note per day, index = day - 1 (plan/itinerary.ts's own
   * ItineraryDay.day is 1-based) — Lee's decision #3/#5: "what we're doing
   * that day," typed into ui/ItinerarySheet.tsx's per-day textarea.
   * Undefined/shorter-than-days arrays mean "no note yet" for the missing
   * days (buildItinerary pads with ""), not a corrupt plan — an older
   * saved plan with no field at all, or a day break added after some notes
   * were written, both decode fine. Deliberately NOT part of the DLRP1
   * share payload (docs/SHARE_LINK_SPEC.md): the spec's own size budget and
   * "control points + waypoints only" contract stop at route geometry, and
   * a note written for one trip's own bookkeeping has no reason to ride a
   * link handed to someone else.
   */
  dayNotes?: string[];
  createdAt: string;
  updatedAt: string;
}

/** Elevation-derived fields are null until terrain samples resolve (or
 * when the state serves no terrain layer). */
export interface PlanStats {
  distanceM: number;
  gainM: number | null;
  lossM: number | null;
  maxGradePct: number | null;
  etaSeconds: number | null;
}

export interface SavedPlan extends Plan {
  id: string;
  savedAt: string;
  /** Snapshot taken at save time so the Plans list renders without
   * re-sampling terrain. */
  stats: PlanStats;
}

/** One elevation sample along the flattened route line. */
export interface ElevationSample {
  at: LonLat;
  /** Cumulative distance from the start, meters. */
  distM: number;
  eleM: number;
}

/** The flattened, renderable geometry of a plan: every leg's endpoints
 * plus via points, deduped at the joins. */
export function planGeometry(plan: Pick<Plan, "points" | "legs">): LonLat[] {
  const out: LonLat[] = [];
  for (let i = 0; i < plan.points.length; i++) {
    out.push(plan.points[i]);
    const leg = plan.legs[i];
    if (leg && i < plan.points.length - 1) out.push(...leg.via);
  }
  return out;
}

export function emptyPlan(region: string, now: string): Plan {
  return {
    name: "",
    region,
    points: [],
    legs: [],
    waypoints: [],
    createdAt: now,
    updatedAt: now,
  };
}
